import { reactive, readonly } from 'vue'
import { firebase } from '@/api/firebase'
import { getDefaultSiteData } from '@/services/siteDefaults'
import { normalizeSkillGroups } from '@/utils/skillGroups'

const siteState = reactive(getDefaultSiteData())
const meta = reactive({ loaded: false, loading: false, saving: false })

function mergeSiteData(remote) {
  const defaults = getDefaultSiteData()
  Object.keys(defaults).forEach((key) => {
    if (key === 'skills') {
      siteState.skills = normalizeSkillGroups(remote?.skills ?? defaults.skills)
      return
    }
    siteState[key] = remote?.[key] ?? defaults[key]
  })
}

export async function loadSiteData() {
  meta.loading = true
  try {
    const remote = await firebase.getValue('site')
    if (remote) {
      mergeSiteData(remote)
    }
  } catch (error) {
    console.error('Failed to load site data', error)
  } finally {
    meta.loaded = true
    meta.loading = false
  }
}

export async function saveSiteData(partial) {
  meta.saving = true
  try {
    Object.assign(siteState, partial)
    const ok = await firebase.insert('site', JSON.parse(JSON.stringify(siteState)))
    if (!ok) throw new Error('Save failed')
    return true
  } finally {
    meta.saving = false
  }
}

export async function saveSiteSection(key, value) {
  meta.saving = true
  try {
    siteState[key] = value
    const ok = await firebase.update_value(`site/${key}`, value)
    if (!ok) throw new Error('Save failed')
    return true
  } finally {
    meta.saving = false
  }
}

export async function seedSiteData() {
  const defaults = getDefaultSiteData()
  mergeSiteData(defaults)
  return saveSiteData(defaults)
}

export function useSiteData() {
  return {
    site: siteState,
    siteState,
    meta: readonly(meta),
    loadSiteData,
    saveSiteData,
    saveSiteSection,
    seedSiteData,
  }
}

export { siteState }
